import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useFirestore } from '../hooks/useFirestore';
import { Pizza, Pizzeria, MapCenter, Location } from '../types';
import Map from '../components/Map';
import { geocodeAddress } from '../utils/geocoding';

export default function PizzeriaDetail() {
  const { id } = useParams<{ id: string }>();
  const { getPizzeria, getPizza } = useFirestore();
  const [pizzeria, setPizzeria] = useState<Pizzeria | null>(null);
  const [pizzas, setPizzas] = useState<Pizza[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLocation, setMapLocation] = useState<Location[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
  
      try {
        setLoading(true);
        setError(null);
        
        const pizzeriaData = await getPizzeria(id);
        if (!pizzeriaData) {
          setPizzeria(null);
          setPizzas([]);
          return;
        }
  
        setPizzeria(pizzeriaData);
        console.log('Pizzeria caricata:', pizzeriaData.name);

        // Gestione coordinate
        let coordinates;
        if (pizzeriaData.lat && pizzeriaData.lng) {
          coordinates = {
            lat: pizzeriaData.lat,
            lng: pizzeriaData.lng
          };
        } else {
          // Se non ci sono coordinate, fa il geocoding
          coordinates = await geocodeAddress(pizzeriaData.address, pizzeriaData.city);
        }

        if (coordinates) {
          setMapLocation([{
            id: pizzeriaData.id,
            lat: coordinates.lat,
            lng: coordinates.lng,
            name: pizzeriaData.name
          }]);
        }
  
        // Prima controlliamo se ci sono pizze nel menu dinamico
        const menuRef = collection(db, 'pizzeinmenu');
        const q = query(
          menuRef, 
          where('pizzeriaId', '==', id)
        );
        const menuSnapshot = await getDocs(q);
  
        if (menuSnapshot.size > 0) {
          // Se ci sono pizze nel menu dinamico, usiamo solo quelle
          console.log('Trovate pizze nel menu dinamico, caricamento...');
          const dynamicPizzaPromises = menuSnapshot.docs
            .filter(doc => !doc.data().isHidden)
            .map(async (doc) => {
              const pizzaId = doc.data().pizzaId;
              return getPizza(pizzaId);
            });
  
          const dynamicPizzaResults = await Promise.all(dynamicPizzaPromises);
          const validPizzas = dynamicPizzaResults.filter((pizza): pizza is Pizza => pizza !== null);
          console.log('Pizze dinamiche caricate:', validPizzas.length);
          setPizzas(validPizzas);
        } else {
          // Se non ci sono pizze nel menu dinamico, usiamo quelle statiche
          console.log('Nessuna pizza nel menu dinamico, uso pizze statiche');
          if (pizzeriaData.pizzas?.length > 0) {
            const staticPizzaPromises = pizzeriaData.pizzas
              .filter(pizzaId => !pizzeriaData.hiddenPizzas?.includes(pizzaId))
              .map(pizzaId => getPizza(pizzaId));
            const staticPizzaResults = await Promise.all(staticPizzaPromises);
            const validPizzas = staticPizzaResults.filter((pizza): pizza is Pizza => pizza !== null);
            console.log('Pizze statiche caricate:', validPizzas.length);
            setPizzas(validPizzas);
          } else {
            setPizzas([]);
          }
        }
  
      } catch (err) {
        console.error('Errore nel caricamento dei dati della pizzeria:', err);
        setError('Si è verificato un errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [id, getPizzeria, getPizza]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Caricamento...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (!pizzeria) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Pizzeria non trovata</div>
      </div>
    );
  }

  const mapCenter = mapLocation.length > 0 ? {
    lat: mapLocation[0].lat,
    lng: mapLocation[0].lng,
    zoom: 15
  } : {
    lat: 41.9028,
    lng: 12.4964,
    zoom: 6
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/" className="text-pizzaveg hover:text-pizzaveg-dark">
            ← Torna alla home
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="relative h-64">
            <img
              src={pizzeria.imageUrl}
              alt={pizzeria.name}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="p-6">
            <h1>{pizzeria.name}</h1>
            <p className="mb-4">
              {pizzeria.description}
            </p>
            <div className="space-y-2">
              <p>
                <span>Indirizzo:</span> {pizzeria.address}
              </p>
              <p>
                <span>Città:</span> {pizzeria.city}
              </p>
              <p>
                <span>Telefono:</span> {pizzeria.phone}
              </p>
              <p>
                <span>Email:</span> {pizzeria.email}
              </p>
            </div>

            <div className="mt-6">
              <h2>Le nostre pizze vegane ({pizzas.length})</h2>
              {pizzas.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pizzas.map(pizza => (
                    <Link
                      key={pizza.id}
                      to={`/pizze/${pizza.id}`}
                      className="block bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <img
                          src={pizza.imageUrl}
                          alt={pizza.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-semibold text-gray-900">{pizza.name}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {pizza.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">
                  Nessuna pizza disponibile al momento.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Mostra la mappa solo se abbiamo le coordinate */}
        {mapLocation.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden h-[400px] relative mt-6">
            <Map 
              locations={mapLocation}
              center={mapCenter}
            />
          </div>
        )}
      </div>
    </div>
  );
}